import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import StaticAdsService from 'services/StaticAdsService';
import AdvertiserPagePicker from 'shared/components/AdvertiserPagePicker';
import AdvertiserPagesDialog from 'shared/components/AdvertiserPagesDialog';
import AdvertiserTargets from 'shared/components/AdvertiserTargets';
import { formatKeywordDate, keywordComparators } from 'shared/keywordSort';
import useSortableTable from 'shared/useSortableTable';

import FollowedBrands from './FollowedBrands';
import ImportStaticsDialog from './ImportStaticsDialog';
import useKeywordSearchUrl from 'shared/useKeywordSearchUrl';
import SearchUrlPreview from 'shared/components/SearchUrlPreview';
import { StaticKeyword } from './types';

// A deliberately leaner sibling of VideoCreative/KeywordManager rather than a
// shared component. That one carries 25+ pieces of state tied to video — an
// embedded scraped-ads table, AdDetailDialog, auto-transcribe — and
// parameterizing it would mean refactoring 682 lines of working, untested code
// to save duplicating straightforward CRUD. The two share the SCRAPE_KEYWORDS
// rows and the API shape, which is where sharing actually pays.

function toYMD(d: Date) { return d.toISOString().split('T')[0]; }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return toYMD(d); }

// Presets store the PRESET NAME, not resolved dates, so the window re-resolves
// relative to today on every scrape — which is what makes a recurring daily
// fetch roll forward instead of re-fetching the same frozen window.
// Must match resolveDatePreset in the collector (src/sbly-api-server/datePreset.js).
// A label missing here renders as a BLANK select on a keyword that has it set,
// and saving the blank silently clears the range — which is how a keyword set to
// "This Year" ended up searching all time.
const DATE_PRESETS = [
  { label: 'All Time', from: '', to: '' },
  { label: 'Last Week', from: daysAgo(7), to: toYMD(new Date()) },
  { label: 'Last Month', from: daysAgo(30), to: toYMD(new Date()) },
  { label: 'Last 3 Months', from: daysAgo(90), to: toYMD(new Date()) },
  { label: 'This Year', from: `${new Date().getFullYear()}-01-01`, to: toYMD(new Date()) },
];

// NOT a preset — the label shown for a row that carries explicit start/end
// dates and no preset (the video tool can save one). Selecting anything else
// replaces that window; leaving this selected leaves the saved dates untouched,
// which is the whole point: editing the NAME of a keyword must not silently
// widen its date range to all time for both tools.
const CUSTOM_RANGE = 'Custom range';

function formatDateRange(kw: StaticKeyword): string {
  if (kw.dateRangePreset) return kw.dateRangePreset;
  if (!kw.startDateMin && !kw.startDateMax) return 'All time';
  return `${kw.startDateMin || '…'} → ${kw.startDateMax || '…'}`;
}

// Owns its own fetch dialog rather than taking an onFetch prop. The tab shell
// renders every tab as <Comp /> with no props by design, so a prop-based trigger
// was never wired up and the per-row button never rendered.
export default function KeywordManager() {
  const service = useMemo(() => new StaticAdsService(), []);

  const [keywords, setKeywords] = useState<StaticKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);


  const [editing, setEditing] = useState<StaticKeyword | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<{ label: string; keyword: string; scrapeDepth: number; datePreset: string; pageIds: string[]; pageGroupIds: string[] }>(
    { label: '', keyword: '', scrapeDepth: 20, datePreset: 'All Time', pageIds: [], pageGroupIds: [] },
  );
  const customRange = form.datePreset === CUSTOM_RANGE;
  // Previewed live off the FORM, not the saved row: the point is to see the
  // search you are about to save, including edits you have not committed.
  const searchPreview = useKeywordSearchUrl(
    body => service.keywordSearchUrl(body),
    {
      keyword: form.keyword,
      // CUSTOM_RANGE is not a preset the server can resolve — preview the
      // explicit window the row carries, which is what saving preserves.
      dateRangePreset: customRange ? undefined : form.datePreset,
      startDateMin: customRange ? editing?.startDateMin : undefined,
      startDateMax: customRange ? editing?.startDateMax : undefined,
      pageIds: form.pageIds,
      pageGroupIds: form.pageGroupIds,
    },
    isNew || !!editing,
  );

  const [confirmRemove, setConfirmRemove] = useState<StaticKeyword | null>(null);
  // The delete is a hard, unrecoverable DELETE of a row Video Creative shares,
  // so the confirmation is acknowledged rather than merely clicked through.
  const [removeAcknowledged, setRemoveAcknowledged] = useState(false);

  const { sorted, sortField, sortDir, handleSort } = useSortableTable<StaticKeyword>(
    keywords, 'createdAt', 'desc', keywordComparators,
  );
  const [managingPages, setManagingPages] = useState(false);
  // Bumped when advertiser pages or groups change, so Followed brands reloads.
  const [pagesVersion, setPagesVersion] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [fetchKeyword, setFetchKeyword] = useState<StaticKeyword | null>(null);
  const [confirmFetch, setConfirmFetch] = useState<StaticKeyword | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await service.listKeywords();
    if (resp) { setKeywords(resp.keywords); setError(null); } else setError('Could not load keywords.');
    setLoading(false);
  }, [service]);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => {
    setIsNew(true);
    setEditing(null);
    setForm({ label: '', keyword: '', scrapeDepth: 20, datePreset: 'All Time', pageIds: [], pageGroupIds: [] });
  };

  const openEdit = (kw: StaticKeyword) => {
    setIsNew(false);
    setEditing(kw);
    setForm({
      label: kw.label || '',
      keyword: kw.keyword,
      scrapeDepth: kw.scrapeDepth ?? 20,
      // An explicit window with no preset is its own selectable value, so
      // saving an unrelated edit keeps it instead of resetting it to All Time.
      datePreset: kw.dateRangePreset || ((kw.startDateMin || kw.startDateMax) ? CUSTOM_RANGE : 'All Time'),
      pageIds: kw.pageIds || [],
      pageGroupIds: kw.pageGroupIds || [],
    });
  };

  const closeDialog = () => { setEditing(null); setIsNew(false); };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const preset = DATE_PRESETS.find(p => p.label === form.datePreset);
      const body = {
        label: form.label.trim() || null,
        keyword: form.keyword.trim(),
        scrapeDepth: form.scrapeDepth,
        pageIds: form.pageIds,
        pageGroupIds: form.pageGroupIds,
        dateRangePreset: customRange ? null : form.datePreset,
        // Null the concrete dates when a preset is chosen so the window
        // re-resolves on each scrape rather than freezing at today's values.
        // CUSTOM_RANGE matches no preset, so both keys stay undefined and are
        // omitted from the request — the API leaves the saved dates alone.
        startDateMin: preset ? null : undefined,
        startDateMax: preset ? null : undefined,
      };
      if (isNew) await service.createKeyword(body);
      else if (editing) await service.updateKeyword(editing.keywordId, body);
      closeDialog();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save the keyword');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (kw: StaticKeyword) => {
    try {
      await service.updateKeyword(kw.keywordId, { isActive: !kw.isActive });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not update the keyword');
    }
  };

  const openConfirmRemove = (kw: StaticKeyword) => { setConfirmRemove(kw); setRemoveAcknowledged(false); };
  const closeConfirmRemove = () => { setConfirmRemove(null); setRemoveAcknowledged(false); };

  const remove = async () => {
    if (!confirmRemove) return;
    try {
      await service.removeKeyword(confirmRemove.keywordId);
      closeConfirmRemove();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not remove the keyword');
    }
  };

  return (
    <Box>
      <FollowedBrands version={pagesVersion} />

      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Search terms fetched from the Facebook Ad Library as static ads. These are the same
            keywords Video Creative uses — a keyword can feed either pipeline, or both.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => setManagingPages(true)}>
            Advertiser pages
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudDownloadOutlinedIcon />}
            onClick={() => { setFetchKeyword(null); setFetching(true); }}
          >
            Fetch statics
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Add keyword
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !keywords.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>No keywords yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Add one here, or just fetch a term from the Library tab — it gets saved automatically.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortField === 'scrapeDepth'} direction={sortField === 'scrapeDepth' ? sortDir : 'asc'} onClick={() => handleSort('scrapeDepth')}>
                    Max ads
                  </TableSortLabel>
                </TableCell>
                <TableCell>Date range</TableCell>
                <TableCell>Advertisers</TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortField === 'isActive'} direction={sortField === 'isActive' ? sortDir : 'asc'} onClick={() => handleSort('isActive')}>
                    Active
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'createdAt'} direction={sortField === 'createdAt' ? sortDir : 'asc'} onClick={() => handleSort('createdAt')}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === 'updatedAt'} direction={sortField === 'updatedAt' ? sortDir : 'asc'} onClick={() => handleSort('updatedAt')}>
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(kw => (
                <TableRow key={kw.keywordId} hover sx={{ opacity: kw.isActive ? 1 : 0.55 }}>
                  <TableCell>
                    {/* The label is what makes a list of search terms scannable;
                        it falls back to the term so an unlabelled row still reads. */}
                    <Typography variant="body2" fontWeight={500}>{kw.label || kw.keyword}</Typography>
                    {kw.label && (
                      <Typography variant="caption" color="text.secondary">{kw.keyword}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{kw.scrapeDepth}</TableCell>
                  <TableCell>{formatDateRange(kw)}</TableCell>
                  <TableCell>
                    <AdvertiserTargets groups={kw.pageGroups} pages={kw.pages} />
                  </TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={!!kw.isActive} onChange={() => toggleActive(kw)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatKeywordDate(kw.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatKeywordDate(kw.updatedAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Fetch statics for this keyword now">
                      <IconButton size="small" onClick={() => setConfirmFetch(kw)}>
                        <CloudDownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(kw)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete this keyword — it is shared, so Video Creative loses it too">
                      <IconButton size="small" color="error" onClick={() => openConfirmRemove(kw)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={isNew || !!editing} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isNew ? 'Add keyword' : 'Edit keyword'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              size="small" label="Name (optional)" fullWidth autoFocus
              placeholder="Mellow — cooling"
              helperText="Shown in the list. Falls back to the search term when blank."
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            />
            <TextField
              size="small" label="Search term" fullWidth
              value={form.keyword}
              onChange={e => setForm(f => ({ ...f, keyword: e.target.value }))}
            />
            <TextField
              size="small" label="Max ads per fetch" type="number"
              value={form.scrapeDepth}
              onChange={e => setForm(f => ({ ...f, scrapeDepth: Number(e.target.value) || 20 }))}
              helperText="A hard cap — collection stops here even if more ads match"

            />
            <TextField
              select size="small" label="Date range"
              value={form.datePreset}
              onChange={e => setForm(f => ({ ...f, datePreset: e.target.value }))}
            >
              {DATE_PRESETS.map(p => <MenuItem key={p.label} value={p.label}>{p.label}</MenuItem>)}
              {/* The row's own explicit window, named by its dates so it is
                  obvious what "keep it as it is" means. */}
              {customRange && (
                <MenuItem value={CUSTOM_RANGE}>
                  {`${CUSTOM_RANGE}: ${editing?.startDateMin || '…'} → ${editing?.startDateMax || '…'}`}
                </MenuItem>
              )}
              {/* A preset this build doesn't know stays selectable rather than
                  rendering blank and being cleared on the next save. */}
              {form.datePreset && !customRange && !DATE_PRESETS.some(p => p.label === form.datePreset) && (
                <MenuItem value={form.datePreset}>{form.datePreset}</MenuItem>
              )}
            </TextField>
            <AdvertiserPagePicker
              pageIds={form.pageIds}
              pageGroupIds={form.pageGroupIds}
              onChange={next => setForm(f => ({ ...f, ...next }))}
            />
            <SearchUrlPreview
              url={searchPreview.url}
              loading={searchPreview.loading}
              advertiserCount={searchPreview.advertiserCount}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.keyword.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmFetch} onClose={() => setConfirmFetch(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fetch statics for &ldquo;{confirmFetch?.label || confirmFetch?.keyword}&rdquo;?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Searches the Ad Library for this term and pulls only STATIC ads into the Static
            Creative library. Video ads are untouched.
          </Typography>
          {!!(confirmFetch?.pageGroups?.length || confirmFetch?.pages?.length) && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Limited to</Typography>
              <AdvertiserTargets groups={confirmFetch?.pageGroups ?? null} pages={confirmFetch?.pages ?? null} max={6} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFetch(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { setFetchKeyword(confirmFetch); setConfirmFetch(null); setFetching(true); }}
          >
            Fetch now
          </Button>
        </DialogActions>
      </Dialog>

      <ImportStaticsDialog
        service={service}
        open={fetching}
        initialKeyword={fetchKeyword}
        onClose={() => { setFetching(false); setFetchKeyword(null); }}
        onFinished={load}
      />

      <AdvertiserPagesDialog
        open={managingPages}
        onClose={() => setManagingPages(false)}
        onChanged={() => { void load(); setPagesVersion(v => v + 1); }}
      />

      <Dialog open={!!confirmRemove} onClose={closeConfirmRemove} maxWidth="xs" fullWidth>
        <DialogTitle>Delete &ldquo;{confirmRemove?.label || confirmRemove?.keyword}&rdquo; permanently?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This deletes the keyword itself — the search term, its max-ads cap, its date range and
            its advertiser filters — and cannot be undone. Ads already fetched stay in their
            libraries.
          </Typography>
          {/* There is one shared SCRAPE_KEYWORDS row per search term and no
              static-only unlink, so this cannot be softened into "removed from
              Static Creative". Say what actually happens, before the click. */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Keywords are shared: <strong>Video Creative loses this keyword too</strong> and stops
            fetching video ads for it.
          </Alert>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Checkbox
                size="small"
                checked={removeAcknowledged}
                onChange={e => setRemoveAcknowledged(e.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2">
                I understand this deletes it from Video Creative as well.
              </Typography>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmRemove}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!removeAcknowledged} onClick={remove}>
            Delete keyword
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
